import AppBaseStore from './baseStore'
import { useDjango } from '@/composables/useDjango'
import type { BaseStoreActions, BaseStoreType } from '@/types/storeTypes'
import type { SectionLine } from '@/stores/type/sectionLine.type'
import API from '@/api/apiUrls'
import { useSelectionPolygonsStores } from '@/stores/selectionPolygon'
import { useGIPointsStore } from '@/stores/giPoints'

const { api } = useDjango()

type State = BaseStoreType<SectionLine>

const baseUrl = API.SECTION_GEO_DATA.SECTION_LINES.INDEX
const baseStore: BaseStoreType<SectionLine> = new AppBaseStore(baseUrl, api)
const baseStoreActions: BaseStoreActions<SectionLine> = baseStore.actions

export const useSectionLinesStore = defineStore({
  id: 'sectionLines',
  state: (): State => ({
    ...baseStore.state,
    loading: false,
    selectedItems: [],
    filterFields: ['name'],
    addPolygonShapeFileDialog: false,
    showSelectMap: {} as Record<number, 'create' | 'remove' | null>,
    updateSectionLineDialog: {
      show: false,
      selectedMode: 'edit'
    },
    newSectionLineCoordinates: null,
    visualiseItem: {},
    reselectSectionLinesDialog: false
  }),
  actions: {
    ...baseStoreActions,
    resetAll() {
      this.$reset()
    },
    bulkDeleteInternal: baseStoreActions.bulkDelete,
    async bulkDelete(items) {
      // Workaround: Add sectionlineuploadcollection to objects for permission checking
      const sectionLineUploadCollection = items[0]?.properties?.section_line_upload_collection
      await this.bulkDeleteInternal(
        items.map((sectionLine) => ({
          ...sectionLine,
          sectionlineuploadcollection: sectionLineUploadCollection
        }))
      )
    },
    toggleSelectedSectionLine(sectionLine: SectionLine, isSelected?: boolean) {
      const index = this.selectedItems.findIndex((item) => item.id === sectionLine.id)
      if (isSelected === undefined) {
        if (index === -1) {
          this.selectedItems.push(sectionLine)
        } else {
          this.selectedItems.splice(index, 1)
        }
      } else {
        if (isSelected && index === -1) {
          this.selectedItems.push(sectionLine)
        } else if (!isSelected && index !== -1) {
          this.selectedItems.splice(index, 1)
        }
      }
    },

    clearSelectedSectionLine() {
      this.selectedItems = []
    },
    selectSectionLinesByCollectionId(collectionId: number) {
      const sectionLines = this.sectionLineByCollectionId(collectionId)
      sectionLines.forEach((sectionLine) => {
        if (!this.selectedItems.some((item) => item.id === sectionLine.id)) {
          this.selectedItems.push(sectionLine)
        }
      })
    },
    deselectSectionLinesByCollectionId(collectionId: number) {
      const sectionLines = this.sectionLineByCollectionId(collectionId)
      sectionLines.forEach((sectionLine) => {
        const index = this.selectedItems.findIndex((item) => item.id === sectionLine.id)
        if (index !== -1) {
          this.selectedItems.splice(index, 1)
        }
      })
    },
    setSectionLineCoordinates(coordinates) {
      this.newSectionLineCoordinates = coordinates
    },
    addHiddenFieldToItems() {
      this.items.forEach((item) => {
        item.hidden = false
      })
    },
    toggleCurrentItem(item: SectionLine) {
      if (this.currentItem === item) {
        this.setCurrentItem({})
      } else this.setCurrentItem(item)
    },
    setVisualiseItem(item: SectionLine) {
      console.log('setVisualiseItem', item)
      this.visualiseItem = item
    },
    toggleVisualiseItem(item: SectionLine) {
      if (this.visualiseItem === item) {
        this.setVisualiseItem({})
      } else this.setVisualiseItem(item)
    },
    toggleSelectionOption(id: number, type: 'create' | 'remove' | null) {
      Object.keys(this.showSelectMap).forEach((key) => {
        const mapKey = parseInt(key)
        if (mapKey !== id) {
          this.showSelectMap[mapKey] = null
        }
      })
      this.showSelectMap[id] = type
    },
    toggleAddPolygonShapeFileDialog() {
      this.addPolygonShapeFileDialog = !this.addPolygonShapeFileDialog
    },
    async uploadPolygonShapeFile(formData) {
      const sectionSelectionPolygonsStore = useSelectionPolygonsStores()
      const responseData = await api.apiService.api(
        API.SECTION_GEO_DATA.SECTION_LINES.UPLOAD_POLYGON_SHAPEFILE,
        'POST',
        formData
      )
      sectionSelectionPolygonsStore.addItems(responseData.features)
      this.toggleAddPolygonShapeFileDialog()
    },
    toggleUpdateSectionLineDialog(selectedMode: 'edit' | 'create' = 'edit') {
      this.updateSectionLineDialog.selectedMode = selectedMode
      this.updateSectionLineDialog.show = !this.updateSectionLineDialog.show
    },
    async generateBufferZone(sectionLineID: number, width: number) {
      const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
      const sectionLineUploadCollection = sectionLineUploadCollectionsStore.items.find((item) =>
        item.sectionline_set.features.find((sectionLine) => sectionLine.id === sectionLineID)
      )
      this.writeLoading = true
      let responseData
      const body = {
        width: width,
        sectionlineuploadcollection: sectionLineUploadCollection.id
      }
      try {
        const uploadUrl =
          API.SECTION_GEO_DATA.SECTION_LINES.INDEX + `${sectionLineID}/generate_buffer/`
        responseData = await api.apiService.api(uploadUrl, 'POST', body)
      } catch (e) {
        this.setSnackbarMessage('Create item failed.', 'error')
        console.error(e)
        this.writeLoading = false
        return false
      }
      this.writeLoading = false
      return responseData
    },
    toggleSelectAllItems(onOff: boolean) {
      this.selectedItems = onOff ? [...this.items] : []
    }
  },
  getters: {
    ...baseStore.getters,
    isSelected: (state: State) => (id: number) => {
      return state.selectedItems.some((item) => item.id === id)
    },
    selectedItemIDs: (state: State) => {
      return state.selectedItems.map((item) => item.id)
    },
    isSelectedAll: (state: State) => {
      const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
      const mappedSelectedCollectionIDs = state.selectedItems.map(
        (item) => item.properties.section_line_upload_collection
      )
      const allCollectionIDs = sectionLineUploadCollectionsStore.items.map((item) => item.id)
      const selectedCollectionIDs = sectionLineUploadCollectionsStore.selectedItems.map(
        (item) => item.id
      )
      const selectedCollectionIDsSet = new Set(mappedSelectedCollectionIDs)
      selectedCollectionIDs.forEach((id) => {
        selectedCollectionIDsSet.add(id)
      })
      const allCollectionIDsSet = new Set(allCollectionIDs)

      return (
        state.selectedItems.length === state.items.length &&
        selectedCollectionIDsSet.size === allCollectionIDsSet.size
      )
    },
    isMultipleSelection: (state: State) => {
      return state.selectedItems.length > 1
    },
    isSingleSelection: (state: State) => {
      return state.selectedItems.length === 1
    },
    sectionLineByCollectionId: (state: State) => (collectionId: number) => {
      return state.items.filter(
        (item) => item.properties.section_line_upload_collection === collectionId
      )
    },
    giPointsBySectionLineId: () => (sectionLineId: number) => {
      const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
      const giPointsStore = useGIPointsStore()
      const selections = sectionLinesGIPointsStore.items.filter(
        (item) => item.section_line === sectionLineId
      )
      return giPointsStore.items.filter((item) =>
        selections.some((selection) => item.id === selection.gi_point)
      )
    },
    filteredGIPointsBySectionLineId: () => (sectionLineId: number) => {
      const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
      const giPointsStore = useGIPointsStore()
      const resultArray = sectionLinesGIPointsStore.items
        .filter((item) => item.section_line === sectionLineId)
        .map((item) => item.gi_point)
      return giPointsStore.items.filter((item) => !resultArray.includes(item.id))
    }
  }
})
